import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, makeAspectCrop, centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (imageUrl: string) => void;
  aspectRatio?: number;
  title?: string;
  uploadPath?: string; // Optional custom upload path (defaults to task-images/)
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export default function ImageCropModal({ 
  isOpen, 
  onClose, 
  onSave, 
  aspectRatio = 1,
  title = 'Crop Image',
  uploadPath
}: ImageCropModalProps) {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspectRatio) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspectRatio));
    }
  };

  const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.width = completedCrop.width * scaleX * pixelRatio;
    canvas.height = completedCrop.height * scaleY * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;

    ctx.drawImage(
      image,
      cropX,
      cropY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
    );

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.95,
      );
    });
  }, [completedCrop]);

  const handleSave = async () => {
    if (!completedCrop) {
      alert('Please crop the image first');
      return;
    }

    setUploading(true);
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      console.log('🔐 ImageCropModal - Current user:', currentUser ? {
        email: currentUser.email,
        uid: currentUser.uid
      } : 'NOT AUTHENTICATED');
      
      if (!currentUser) {
        alert('❌ NOT LOGGED IN!\n\nYou must be logged in to upload images.\nPlease refresh the page and log in again.');
        setUploading(false);
        return;
      }

      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        alert('Failed to crop image');
        setUploading(false);
        return;
      }

      // Upload to Firebase Storage
      // If uploadPath is provided (for contests), use it directly
      // Otherwise (for tasks), generate a filename and use task-images/
      let storagePath: string;
      if (uploadPath) {
        storagePath = uploadPath; // For contests: contests_rewards/{id}/image or /banner
      } else {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.jpg`;
        storagePath = `task-images/${fileName}`; // For tasks: task-images/{filename}
      }
      console.log('📤 Uploading to path:', storagePath);
      console.log('📤 File size:', croppedBlob.size, 'bytes');
      console.log('📤 File type:', croppedBlob.type);
      
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, croppedBlob);
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ Upload successful! URL:', downloadURL);

      onSave(downloadURL);
      handleClose();
    } catch (error: any) {
      console.error('❌ Error uploading image:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error server response:', error.serverResponse);
      
      if (error.code === 'storage/unauthorized' || error.code === 'storage/permission-denied') {
        const currentUser = auth.currentUser;
        const authStatus = currentUser ? `Logged in as: ${currentUser.email}` : 'NOT LOGGED IN';
        
        alert(`❌ STORAGE PERMISSION DENIED!\n\n${authStatus}\n\nYour Firebase Storage rules are blocking this upload.\n\n🔧 YOU MUST FIX THIS IN FIREBASE CONSOLE:\n\n1. Go to: https://console.firebase.google.com/project/pixano-ff250/storage/rules\n2. Make sure you have this rule:\n   match /task-images/{allPaths=**} {\n     allow read, write: if request.auth != null;\n   }\n3. Click "PUBLISH" (top right)\n4. Wait 30-60 seconds for rules to propagate\n5. Try again\n\nIf you already did this, check:\n- Rules syntax is correct (no red underlines)\n- Rules were actually published\n- You're logged in with the correct account`);
      } else {
        alert(`Error uploading image: ${error.code || 'Unknown'} - ${error.message || 'Unknown error'}\n\nCheck browser console for details.`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={uploading}
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {!imgSrc ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500"
              />
              <p className="text-sm text-gray-500 mt-2 text-center">
                Select an image to crop
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  minWidth={50}
                  minHeight={50}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    style={{ maxHeight: '60vh', maxWidth: '100%' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setImgSrc('');
                    setCrop(undefined);
                    setCompletedCrop(undefined);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  disabled={uploading}
                >
                  Choose Different Image
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            disabled={uploading}
          >
            Cancel
          </button>
          {imgSrc && (
            <button
              onClick={handleSave}
              disabled={uploading || !completedCrop}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Save & Upload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

